import {
  useWSModalCustom,
  useWSSnackbar,
  WSForm,
  WSInputDate,
  WSList,
  WSModal,
  WSModalCustomProps
} from "@wingspanhq/fe-component-library";
import { IScheduleDate } from "@wingspanhq/payments/dist/interfaces/invoice";
import { ScheduleStatus } from "@wingspanhq/payments/dist/interfaces";
import React from "react";
import * as Yup from "yup";
import { WSErrorMessage } from "../../../../components/WSErrorMessage/WSErrorMessage";
import { useUserId } from "../../../../query/hooks/helpers";
import { useMutationUpdatePayrollSettings } from "../../../../query/payments/mutations";
import { useQueryPayrollSettings } from "../../../../query/payments/queries";
import { buildScheduleDatesUpdate } from "../../../utils/payables";

type FormData = {
  checkDate: Date;
};

type Props = {
  scheduleDate: IScheduleDate;
} & WSModalCustomProps<boolean>;

const ModalEditScheduleDate: React.FC<Props> = ({ scheduleDate, onClose }) => {
  const userId = useUserId();
  const { openSnackbar } = useWSSnackbar();
  // Read the schedule at submit time rather than trusting a snapshot taken when
  // the modal opened — the run is located by its own dates, so the write always
  // lands on the row the user actually opened. See buildScheduleDatesUpdate.
  const queryPayrollSettings = useQueryPayrollSettings(userId);
  const [updatePayrollSettings, updatePayrollSettingsMeta] =
    useMutationUpdatePayrollSettings(userId);

  const handleSubmit = async ({ checkDate }: FormData) => {
    const scheduleDates = buildScheduleDatesUpdate({
      scheduleDates: queryPayrollSettings.data?.scheduleDates,
      scheduleDate,
      update: {
        date: checkDate,
        status: ScheduleStatus.Modified
      }
    });

    // The run vanished from the schedule while the modal was open — writing
    // would land on the wrong position, so bail instead of guessing.
    if (!scheduleDates) {
      openSnackbar({
        type: "warning",
        message: "This payroll run is no longer available"
      });
      onClose(false);
      return;
    }

    await updatePayrollSettings(
      { scheduleDates },
      {
        onSuccess: () => {
          onClose(true);
        }
      }
    );
  };

  return (
    <WSForm<FormData>
      defaultValues={{ checkDate: scheduleDate.date }}
      validationSchema={Yup.object().shape({
        checkDate: Yup.date().required("Required").typeError("Required")
      })}
      onSubmit={handleSubmit}
    >
      <WSModal
        title="Edit payroll date"
        size="XS"
        onClose={() => onClose(false)}
        actions={{
          buttons: [
            {
              label: "Cancel",
              kind: "Secondary",
              type: "button",
              onClick: () => onClose(false)
            },
            {
              label: "Update",
              kind: "Primary",
              type: "submit",
              loading: updatePayrollSettingsMeta.isLoading
            }
          ]
        }}
      >
        <WSList gap="M">
          <WSForm.Field
            name="checkDate"
            label="Expected pay date"
            component={WSInputDate}
          />

          <WSErrorMessage
            contextKey="PayrollSettings"
            error={updatePayrollSettingsMeta.error}
          />
        </WSList>
      </WSModal>
    </WSForm>
  );
};

export const useModalEditScheduleDate = () =>
  useWSModalCustom(ModalEditScheduleDate);
